import React, { useState } from "react";
import {
  Button,
  Flex,
  Popconfirm,
  Space,
  Table,
  Typography,
  Tag,
  Input,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  useGetAllMeetingRoom,
  useRemoveMeetingRoom,
} from "../../query/HrmQuery";
import ModalAddMeetingRoom from "../../components/modal/hrm/ModalAddMeetingRoom";
import { notify } from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { IMeetingRoomQuery } from "../../models/Hrm";
import { debounce } from "lodash";

const { Text } = Typography;

const HrmMeetingRoom = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<any>(null);
  const navigate = useNavigate();
  const [filter, setFilter] = useState<IMeetingRoomQuery>({
    limit: 10,
    page: 1,
  });
  const { data: roomsResponse, isLoading } = useGetAllMeetingRoom(filter);

  const removeMutation = useRemoveMeetingRoom();

  const rooms = roomsResponse?.data || [];

  const handleDelete = async (id: number | string) => {
    try {
      await removeMutation.mutateAsync(id);
      notify.success("Xóa phòng họp thành công");
    } catch (error) {
      notify.error("Xóa phòng họp thất bại");
    }
  };
  const handleSearch = (field: keyof IMeetingRoomQuery) => (value: any) => {
    debouncedSearch(field, value);
  };

  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof IMeetingRoomQuery, value: any) => {
        setFilter((prev) => ({
          ...prev,
          [field]: value === "" ? undefined : (value ?? undefined),
          page: 1,
        }));
      }, 300),
    [setFilter],
  );
  const columns = [
    {
      title: "Mã",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: filter.name ? `Tên phòng (${filter.name})` : "Tên phòng",
      dataIndex: "name",
      key: "name",
      filterDropdown: () => (
        <Input
          placeholder="Tìm tên phòng"
          onChange={(e) => handleSearch("name")(e.target.value)}
          prefix={<SearchOutlined />}
          style={{ padding: 8 }}
        />
      ),
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: filter.floor ? `Tầng (${filter.floor})` : "Tầng",
      dataIndex: "floor",
      key: "floor",
      filterDropdown: () => (
        <Input
          placeholder="Tìm tầng"
          onChange={(e) => handleSearch("floor")(e.target.value)}
          prefix={<SearchOutlined />}
          style={{ padding: 8 }}
        />
      ),
    },
    {
      title: filter.location ? `Vị trí (${filter.location})` : "Vị trí",
      dataIndex: "location",
      key: "location",
      filterDropdown: () => (
        <Input
          placeholder="Tìm vị trí"
          onChange={(e) => handleSearch("location")(e.target.value)}
          prefix={<SearchOutlined />}
          style={{ padding: 8 }}
        />
      ),
    },
    {
      title: "Sức chứa",
      dataIndex: "capacity",
      key: "capacity",
      render: (val: number) => (val ? `${val} người` : "-"),
    },
    {
      title:
        filter.isAvailable !== undefined
          ? `Trạng thái (${filter.isAvailable ? "Đang hoạt động" : "Tạm ngưng"})`
          : "Trạng thái",
      dataIndex: "isAvailable",
      key: "isAvailable",
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Select
            className="w-[150px]"
            placeholder="Chọn trạng thái"
            value={filter.isAvailable}
            onChange={(value) => handleSearch("isAvailable")(value)}
            allowClear
            options={[
              { value: true, label: "Đang hoạt động" },
              { value: false, label: "Tạm ngưng" },
            ]}
          />
        </div>
      ),
      render: (isAvailable: boolean) => (
        <Tag color={isAvailable ? "success" : "error"}>
          {isAvailable ? "Đang hoạt động" : "Tạm ngưng"}
        </Tag>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined className="text-blue-500" />}
            onClick={() => {
              setEditingRoom(record);
              setIsModalOpen(true);
            }}
          />
          <Popconfirm
            title="Xóa phòng họp"
            description="Bạn có chắc chắn muốn xóa phòng họp này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Flex vertical className="p-4 h-full overflow-hidden">
      <Flex
        justify="space-between"
        align="center"
        className="mb-4"
        wrap="wrap"
        gap="small"
      >
        <Button
          type="default"
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Quay lại
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRoom(null);
            setIsModalOpen(true);
          }}
        >
          Thêm phòng họp
        </Button>
      </Flex>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rooms}
        loading={isLoading}
        pagination={{
          current: filter.page,
          pageSize: filter.limit,
          total: roomsResponse?.pagination?.total,
          onChange: (page, pageSize) => {
            setFilter((prev) => ({ ...prev, page, limit: pageSize }));
          },
        }}
        scroll={{ x: "max-content", y: "calc(100vh - 250px)" }}
        className="shadow-sm border border-gray-100 rounded-lg overflow-hidden"
      />

      <ModalAddMeetingRoom
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSuccess={() => setIsModalOpen(false)}
        initialData={editingRoom}
      />
    </Flex>
  );
};

export default HrmMeetingRoom;
